import { useState, useEffect, useRef } from 'react'
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet'
import L from 'leaflet'
import { useLiveQuery } from 'dexie-react-hooks'
import { db } from '../services/db'
import { showAppSelector } from '../services/taxiService'
import { tmbService } from '../services/tmbService'

const DEFAULT_CENTER = [41.3851, 2.1734]
const DEFAULT_ZOOM   = 13

// ── Icones ────────────────────────────────────────────────────────────────────

const icoUbicacio = L.divIcon({
  className: '',
  html: `<div style="width:16px;height:16px;background:#007AFF;border:3px solid #fff;border-radius:50%;box-shadow:0 0 0 4px rgba(0,122,255,0.25)"></div>`,
  iconSize: [16, 16], iconAnchor: [8, 8],
})

function createStopIcon(lines) {
  const txt = (lines ?? []).slice(0, 2).join(',')
  return L.divIcon({
    className: '',
    html: `<div style="background:#E3000B;color:#fff;border-radius:4px;padding:2px 5px;font-size:10px;font-weight:bold;border:1.5px solid #fff;box-shadow:0 2px 4px rgba(0,0,0,0.3);white-space:nowrap">🚌 ${txt}</div>`,
    iconSize: [null, 20],
    iconAnchor: [0, 10],
    popupAnchor: [0, -12],
  })
}

function createNumberedIcon(number, estat) {
  const color = ESTAT_COLORS[estat] ?? '#007AFF'
  return L.divIcon({
    className: '',
    html: `<div style="background:${color};color:#fff;border-radius:50%;width:28px;height:28px;display:flex;align-items:center;justify-content:center;font-weight:bold;font-size:13px;border:2px solid #fff;box-shadow:0 2px 4px rgba(0,0,0,0.3)">${number}</div>`,
    iconSize: [28, 28], iconAnchor: [14, 14], popupAnchor: [0, -16],
  })
}

function createStartIcon() {
  return L.divIcon({
    className: '',
    html: `<div style="background:#34C759;color:#fff;border-radius:50%;width:28px;height:28px;display:flex;align-items:center;justify-content:center;font-size:14px;border:2px solid #fff;box-shadow:0 2px 4px rgba(0,0,0,0.3)">🏠</div>`,
    iconSize: [28, 28], iconAnchor: [14, 14], popupAnchor: [0, -16],
  })
}

function createHospitalIcon() {
  return L.divIcon({
    className: '',
    html: `<div style="background:#FF3B30;color:#fff;border-radius:8px;width:32px;height:28px;display:flex;align-items:center;justify-content:center;font-size:14px;border:2px solid #fff;box-shadow:0 2px 4px rgba(0,0,0,0.3)">🏥</div>`,
    iconSize: [32, 28], iconAnchor: [16, 14], popupAnchor: [0, -16],
  })
}

const COLORS_RUTA  = ['#007AFF','#ff3b30','#ff9500','#34c759','#af52de','#5ac8fa','#ffcc00','#ff2d55']
function colorOrdre(i) { return COLORS_RUTA[i % COLORS_RUTA.length] }

const ESTAT_COLORS = { ingres: '#34C759', alta: '#FF3B30' }

// ── Helpers ───────────────────────────────────────────────────────────────────

function haversineDistance(lat1, lng1, lat2, lng2) {
  const R  = 6371e3
  const φ1 = lat1 * Math.PI / 180
  const φ2 = lat2 * Math.PI / 180
  const Δφ = (lat2 - lat1) * Math.PI / 180
  const Δλ = (lng2 - lng1) * Math.PI / 180
  const a  = Math.sin(Δφ/2)**2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ/2)**2
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

function estimateTime(metres, tipus) {
  const speeds = { peu: 5000/60, transport: 20000/60, híbrida: 12000/60 }
  return Math.round(metres / (speeds[tipus] ?? speeds.peu))
}

function fmtDist(m) {
  return m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${Math.round(m)} m`
}

// ── Components interns ────────────────────────────────────────────────────────

function FlyToUser({ position }) {
  const map = useMap()

  useEffect(() => {
    if (
      position &&
      position.lat !== undefined &&
      position.lng !== undefined &&
      !isNaN(position.lat) &&
      !isNaN(position.lng)
    ) {
      map.flyTo([position.lat, position.lng], 15)
    }
  }, [position, map])

  return null
}

function MapInvalidate({ visible, showPanel }) {
  const map = useMap()
  useEffect(() => {
    setTimeout(() => map.invalidateSize(), 80)
  }, [visible, showPanel, map])
  return null
}

function TaxiModal({ domicili, onClose }) {
  const lat    = domicili?.dades_json?.lat
  const lng    = domicili?.dades_json?.lng
  const adreca = domicili?.adreça ?? ''

  if (!lat || !lng) {
    return (
      <div style={S.modalFons} onClick={onClose}>
        <div style={S.modalSheet} onClick={e => e.stopPropagation()}>
          <p style={{ color: '#6b7280', textAlign: 'center', padding: '8px 0' }}>
            {domicili?.codi} no té coordenades guardades.
          </p>
          <button style={S.btnCancel} onClick={onClose}>Tancar</button>
        </div>
      </div>
    )
  }

  const opcions = showAppSelector(lat, lng, adreca)
  return (
    <div style={S.modalFons} onClick={onClose}>
      <div style={S.modalSheet} onClick={e => e.stopPropagation()}>
        <div style={S.handle} />
        <h3 style={S.modalTitol}>Anar a {domicili.codi}</h3>
        <p style={S.modalSub}>{adreca}</p>
        {opcions.map(o => (
          <button key={o.id} style={S.opcioBtn} onClick={() => { o.onPress(); onClose() }}>
            <span style={{ fontSize: 20 }}>{o.emoji}</span>
            <span style={{ fontSize: 15, fontWeight: 500 }}>{o.label}</span>
          </button>
        ))}
        <button style={S.btnCancel} onClick={onClose}>Cancel·lar</button>
      </div>
    </div>
  )
}

function TmbModal({ domicili, onClose }) {
  const [stops, setStops] = useState(null)
  const [error, setError] = useState(null)
  const lat0 = domicili.dades_json?.lat
  const lng0 = domicili.dades_json?.lng

  useEffect(() => {
    if (!lat0 || !lng0) { setStops([]); return }
    tmbService.getNearbyStops(lat0, lng0, 400)
      .then(data => {
        const features = data?.features ?? []
        const amb = features
          .map(f => {
            const [fLng, fLat] = f.geometry?.coordinates ?? [0, 0]
            return { ...f, _dist: Math.round(haversineDistance(lat0, lng0, fLat, fLng)) }
          })
          .sort((a, b) => a._dist - b._dist)
          .slice(0, 8)
        setStops(amb)
      })
      .catch(() => setError('No s\'han pogut carregar les parades TMB'))
  }, [lat0, lng0])

  return (
    <div style={S.modalFons} onClick={onClose}>
      <div style={S.modalSheet} onClick={e => e.stopPropagation()}>
        <div style={S.handle} />
        <h3 style={S.modalTitol}>🚌 Parades prop de {domicili.codi}</h3>
        {!stops && !error && <p style={S.tmbMsg}>Carregant…</p>}
        {error && <p style={{ ...S.tmbMsg, color: '#ef4444' }}>{error}</p>}
        {stops?.length === 0 && !error && <p style={S.tmbMsg}>Cap parada en 400 m</p>}
        <div style={{ maxHeight: 240, overflowY: 'auto' }}>
          {stops?.map((s, i) => (
            <div key={s.properties?.CODI_PARADA ?? i} style={S.stopFila}>
              <span style={S.stopNom}>{s.properties?.NOM_PARADA ?? '—'}</span>
              <span style={S.stopDist}>{s._dist} m</span>
            </div>
          ))}
        </div>
        <button style={S.btnCancel} onClick={onClose}>Tancar</button>
      </div>
    </div>
  )
}

const TIPUS_ICON = { WALK: '🚶', BUS: '🚌', SUBWAY: '🚇', TRAM: '🚊', RAIL: '🚆' }

function SegmentPlanner({ leg }) {
  const { itinerari } = leg
  if (!itinerari) {
    return (
      <div style={S.segmentFila}>
        <span style={S.segmentText}>
          {`↓ ${leg.mins} min · ${fmtDist(leg.dist)}`}
        </span>
      </div>
    )
  }

  return (
    <div style={S.plannerBloc}>
      {itinerari.segments.map((seg, i) => {
        const icon  = TIPUS_ICON[seg.tipus] ?? '🔹'
        const color = seg.color ?? (seg.tipus === 'WALK' ? '#888' : '#007AFF')
        return (
          <div key={i} style={S.plannerSeg}>
            <div style={{ ...S.plannerBarra, backgroundColor: color }} />
            <div style={S.plannerInfo}>
              <span style={S.plannerTipus}>
                {icon}
                {seg.linia && <span style={{ ...S.plannerLinia, backgroundColor: color }}>{seg.linia}</span>}
                {' '}{seg.origen.nom}
                {seg.tipus !== 'WALK' && seg.desti?.nom && ` → ${seg.desti.nom}`}
              </span>
              <span style={S.plannerTemps}>
                {seg.duracio} min
                {seg.temps_espera_real != null && (
                  <span style={S.plannerRealTime}> · ⏱️ {seg.temps_espera_real} min ara</span>
                )}
              </span>
            </div>
          </div>
        )
      })}
      <div style={S.plannerResum}>
        Total: {itinerari.duracio_total} min
        {itinerari.transbords > 0 && ` · ${itinerari.transbords} transbord${itinerari.transbords > 1 ? 's' : ''}`}
      </div>
    </div>
  )
}

function ItinerariPanel({ domicilis, sequencia, ruta, legs, onFlyTo, onGuiar, onTaxi, onTmb }) {
  const tipus = ruta?.tipus ?? 'peu'

  // Si hi ha sequencia tipada, l'usem; sinó, construïm a partir de domicilis (backward compat)
  const punts = sequencia ?? domicilis.map((dom, i) => ({
    codi:      dom.codi,
    tipus:     'domicili',
    id:        dom.id,
    nom:       dom.dades_json?.persona_nom ?? null,
    adreca:    dom.adreça ?? null,
    lat:       dom.dades_json?.lat ?? null,
    lng:       dom.dades_json?.lng ?? null,
    dades_json: dom.dades_json ?? null,
  }))

  // L'offset de legs: si hi ha punt d'inici, els legs comencen des del segon punt
  const legOffset = punts.findIndex(p => p.tipus !== 'inici')

  // Si no hi ha legs del planner, calcula per haversine com a fallback
  const legsEfectius = legs ?? (() => {
    const arr = []
    for (let i = legOffset; i < punts.length - 1; i++) {
      const a = { lat: punts[i].lat, lng: punts[i].lng }
      const b = { lat: punts[i + 1].lat, lng: punts[i + 1].lng }
      if (a.lat && a.lng && b.lat && b.lng) {
        const dist = haversineDistance(a.lat, a.lng, b.lat, b.lng)
        arr.push({ dist, mins: estimateTime(dist, tipus), itinerari: null })
      } else {
        arr.push(null)
      }
    }
    return arr
  })()

  const totalMins = legsEfectius.reduce((s, l) => s + (l?.mins ?? 0), 0)
  const totalDist = legsEfectius.reduce((s, l) => s + (l?.dist ?? 0), 0)
  const numDomicilis = punts.filter(p => p.tipus === 'domicili').length
  let domIdx = 0

  return (
    <div style={S.panel}>
      <div style={S.panelHeader}>
        <span style={S.panelTitol}>Itinerari · {numDomicilis} domicilis</span>
        {ruta?.hora_inici && <span style={S.panelHora}>📍 {ruta.hora_inici}</span>}
      </div>

      <div style={S.panelLlista}>
        {punts.map((punt, i) => {
          const isLast  = i === punts.length - 1
          const legIdx  = i - legOffset
          const leg     = legIdx >= 0 ? legsEfectius[legIdx] : null

          if (punt.tipus === 'inici') {
            return (
              <div key={`punt-${i}`} style={S.paradaBloc}>
                <div style={S.paradaFila} onClick={() => punt.lat && punt.lng && onFlyTo(punt.lat, punt.lng)}>
                  <div style={{ ...S.paradaNum, backgroundColor: '#34C759' }}>🏠</div>
                  <div style={S.paradaInfo}>
                    <span style={S.paradaCodi}>Punt d'inici</span>
                    {punt.adreca && <span style={S.paradaAdreca}>{punt.adreca}</span>}
                  </div>
                </div>
                <div style={S.segmentFila}><span style={S.segmentText}>↓</span></div>
              </div>
            )
          }

          if (punt.tipus === 'hospital') {
            return (
              <div key={`punt-${i}`} style={S.paradaBloc}>
                <div style={S.paradaFila} onClick={() => punt.lat && punt.lng && onFlyTo(punt.lat, punt.lng)}>
                  <div style={{ ...S.paradaNum, backgroundColor: '#FF3B30', borderRadius: 6 }}>🏥</div>
                  <div style={S.paradaInfo}>
                    <span style={S.paradaCodi}>{punt.nom ?? 'Hospital'}</span>
                    {punt.adreca && <span style={S.paradaAdreca}>{punt.adreca}</span>}
                  </div>
                </div>
                {!isLast && (
                  leg
                    ? <SegmentPlanner leg={leg} />
                    : <div style={S.segmentFila}><span style={S.segmentText}>↓</span></div>
                )}
              </div>
            )
          }

          // Domicili normal
          const domNum = ++domIdx
          const domObj = punt.id ? (domicilis.find(d => d.id === punt.id) ?? punt) : punt
          return (
            <div key={`punt-${i}`} style={S.paradaBloc}>
              <div style={S.paradaFila} onClick={() => punt.lat && punt.lng && onFlyTo(punt.lat, punt.lng)}>
                <div style={{ ...S.paradaNum, backgroundColor: colorOrdre(domNum - 1) }}>
                  {isLast ? '🏁' : domNum}
                </div>
                <div style={S.paradaInfo}>
                  <span style={S.paradaCodi}>{punt.codi}</span>
                  {punt.adreca && <span style={S.paradaAdreca}>{punt.adreca}</span>}
                  {punt.nom && <span style={S.paradaNom}>👤 {punt.nom}</span>}
                  {domObj.dades_json?.telefon && (
                    <a href={`tel:${domObj.dades_json.telefon.replace(/\s/g, '')}`}
                       style={S.paradaTel} onClick={e => e.stopPropagation()}>
                      📞 {domObj.dades_json.telefon}
                    </a>
                  )}
                  {(punt.dades_json?.estat ?? domObj.dades_json?.estat) === 'ingres' && (
                    <span style={S.badgeIngres}>🟢 NOU INGRÉS</span>
                  )}
                  {(punt.dades_json?.estat ?? domObj.dades_json?.estat) === 'alta' && (
                    <span style={S.badgeAlta}>🔴 ALTA</span>
                  )}
                </div>
                <div style={S.paradaBtns}>
                  {punt.lat && punt.lng && (
                    <button style={S.btnPetit} title="Guiar" onClick={e => { e.stopPropagation(); onGuiar(domObj) }}>🧭</button>
                  )}
                  {punt.lat && punt.lng && (
                    <button style={S.btnPetit} title="Parades TMB" onClick={e => { e.stopPropagation(); onTmb(domObj) }}>🚌</button>
                  )}
                  <button style={S.btnPetit} title="Taxi" onClick={e => { e.stopPropagation(); onTaxi(domObj) }}>🚕</button>
                </div>
              </div>
              {!isLast && (
                leg
                  ? <SegmentPlanner leg={leg} />
                  : <div style={S.segmentFila}><span style={S.segmentText}>↓ sense coordenades</span></div>
              )}
            </div>
          )
        })}

        {totalMins > 0 && (
          <div style={S.panelTotal}>
            ⏱️ Total estimat: <strong>{totalMins} min</strong>
            {totalDist > 0 && ` · ${fmtDist(totalDist)}`}
          </div>
        )}
      </div>
    </div>
  )
}

// ── Pàgina principal ──────────────────────────────────────────────────────────

async function fetchParadesAgrupades(domicilis) {
  const perDom = await Promise.all(
    domicilis
      .filter(d => d.dades_json?.lat && d.dades_json?.lng)
      .map(d =>
        tmbService.getNearbyStops(d.dades_json.lat, d.dades_json.lng, 400)
          .then(data => {
            const features = data?.features ?? []
            const byCode = {}
            for (const f of features) {
              const codi = f.properties?.CODI_PARADA
              if (!codi) continue
              if (!byCode[codi]) {
                const [fLng, fLat] = f.geometry?.coordinates ?? [0, 0]
                byCode[codi] = { id: String(codi), nom: f.properties?.NOM_PARADA ?? String(codi), lat: fLat, lng: fLng, lines: [] }
              }
              const linia = f.properties?.CODI_LINIA ?? f.properties?.NOM_LINIA
              if (linia) {
                const key = String(linia)
                if (!byCode[codi].lines.includes(key)) byCode[codi].lines.push(key)
              }
            }
            return Object.values(byCode)
          })
          .catch(() => [])
      )
  )
  const seen = new Set()
  return perDom.flat().filter(p => { if (seen.has(p.id)) return false; seen.add(p.id); return true })
}

export default function MapPage({ selectedRutaId, onChangeRuta, onBack, visible }) {
  const [posUsuari, setPosUsuari]         = useState(null)
  const [flyTo, setFlyTo]                 = useState(null)
  const [taxiDom, setTaxiDom]             = useState(null)
  const [tmbDom, setTmbDom]               = useState(null)
  const [gpsEstat, setGpsEstat]           = useState('desconegut') // 'desconegut'|'actiu'|'denegat'
  const [mostrarParades, setMostrarParades] = useState(false)
  const [paradesMap, setParadesMap]       = useState([])
  const [paradaArrivals, setParadaArrivals] = useState({})
  const [loadingParada, setLoadingParada]   = useState(null)
  const watchIdRef = useRef(null)

  const rutes = useLiveQuery(
    () => db.rutes.orderBy('data').reverse().toArray(), []
  ) ?? []

  const rutaSeleccionada = useLiveQuery(
    () => selectedRutaId ? db.rutes.get(Number(selectedRutaId)) : Promise.resolve(null),
    [selectedRutaId]
  ) ?? null

  const domicilisRuta = useLiveQuery(async () => {
    if (!selectedRutaId) return []
    const anots  = await db.anotacions.where('ruta_id').equals(Number(selectedRutaId)).toArray()
    const sorted = anots.sort((a, b) => (a.ordre ?? 0) - (b.ordre ?? 0))
    const ids    = sorted.map(a => a.domicili_id)
    if (!ids.length) return []
    const doms   = await db.domicilis.where('id').anyOf(ids).toArray()
    return ids.map(id => doms.find(d => d.id === id)).filter(Boolean)
  }, [selectedRutaId]) ?? []

  // Parades TMB: es carreguen quan l'usuari activa el toggle
  useEffect(() => {
    if (!mostrarParades || !domicilisRuta.length) {
      if (!mostrarParades) setParadesMap([])
      return
    }
    fetchParadesAgrupades(domicilisRuta).then(setParadesMap)
  }, [mostrarParades, domicilisRuta])

  // Geolocalització — opcional, s'activa només quan l'usuari fa tap
  function iniciarSeguiment() {
    const wid = navigator.geolocation.watchPosition(
      pos => {
        const lat = pos.coords?.latitude
        const lng = pos.coords?.longitude
        if (lat == null || lng == null || isNaN(lat) || isNaN(lng)) return
        setPosUsuari([lat, lng])
      },
      () => {},
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 5000 }
    )
    watchIdRef.current = wid
  }

  function activarUbicacio() {
    if (!navigator.geolocation) return
    navigator.geolocation.getCurrentPosition(
      pos => {
        const lat = pos.coords.latitude
        const lng = pos.coords.longitude
        setPosUsuari([lat, lng])
        setFlyTo({ lat, lng })
        setGpsEstat('actiu')
        iniciarSeguiment()
      },
      () => setGpsEstat('denegat'),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    )
  }

  useEffect(() => {
    return () => {
      if (watchIdRef.current != null) navigator.geolocation.clearWatch(watchIdRef.current)
    }
  }, [])

  async function handleParadaClick(parada) {
    if (paradaArrivals[parada.id] !== undefined) return
    setLoadingParada(parada.id)
    try {
      const data = await tmbService.getStopArrivals(parada.id)
      const arrivals = (data?.data?.ibus ?? []).map(bus => ({
        linia:      bus.line,
        destinacio: bus.destination,
        minuts:     bus['t-in-min'] ?? 0,
      }))
      const linies = [...new Set(arrivals.map(a => a.linia).filter(Boolean))]
      setParadaArrivals(prev => ({ ...prev, [parada.id]: arrivals }))
      if (linies.length > 0) {
        setParadesMap(prev => prev.map(p =>
          p.id === parada.id ? { ...p, lines: linies } : p
        ))
      }
    } catch {
      setParadaArrivals(prev => ({ ...prev, [parada.id]: [] }))
    } finally {
      setLoadingParada(null)
    }
  }

  function guiar(dom) {
    const { lat, lng } = dom.dades_json ?? {}
    if (!lat || !lng) return
    const ios = /iphone|ipad|ipod/i.test(navigator.userAgent)
    window.open(
      ios
        ? `maps://?daddr=${lat},${lng}&dirflg=d`
        : `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving`,
      '_blank'
    )
  }

  // Seqüència tipada (nova) i legs del planner
  const sequenciaData = (() => {
    try { return rutaSeleccionada?.sequencia_json ? JSON.parse(rutaSeleccionada.sequencia_json) : null }
    catch { return null }
  })()
  const legsData = (() => {
    try { return rutaSeleccionada?.legs_json ? JSON.parse(rutaSeleccionada.legs_json) : null }
    catch { return null }
  })()

  // Punts a mostrar: seqüència nova o fallback a domicilisRuta
  const puntsRuta = sequenciaData
    ?? domicilisRuta.map(dom => ({
      codi: dom.codi, tipus: 'domicili', id: dom.id,
      lat:  dom.dades_json?.lat ?? null, lng: dom.dades_json?.lng ?? null,
      dades_json: dom.dades_json,
    }))

  const showPanel = !!(selectedRutaId && puntsRuta.length > 0)

  // Offset de legs (els legs comencen des del primer punt no-inici)
  const legOffset = puntsRuta.findIndex(p => p.tipus !== 'inici')

  // Fallback de polilínia (sense dades del planner)
  const routeCoords = puntsRuta
    .filter(p => p.lat && p.lng)
    .map(p => [p.lat, p.lng])

  return (
    <div style={S.page}>

      {/* Barra superior */}
      <div style={S.topBar}>
        {onBack && (
          <button style={S.backBtn} onClick={onBack}>←</button>
        )}
        <select
          style={S.rutaSelect}
          value={selectedRutaId ?? ''}
          onChange={e => onChangeRuta?.(e.target.value ? Number(e.target.value) : null)}
        >
          <option value="">— Selecciona una ruta —</option>
          {rutes.map(r => (
            <option key={r.id} value={r.id}>{r.nom} {r.data ? `(${r.data})` : ''}</option>
          ))}
        </select>
        {selectedRutaId && (
          <span style={S.comptador}>{puntsRuta.filter(p => p.tipus === 'domicili').length}</span>
        )}
      </div>

      {/* Àrea del mapa: ocupa 60% quan hi ha panel, 100% si no */}
      <div style={{ ...S.mapWrapper, flex: showPanel ? 3 : 1 }}>
        <MapContainer center={posUsuari ?? DEFAULT_CENTER} zoom={DEFAULT_ZOOM} zoomControl={false} style={S.map}>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            maxZoom={19}
          />
          <MapInvalidate visible={!!visible} showPanel={showPanel} />
          {flyTo && <FlyToUser position={flyTo} />}

          {/* Marcador de la posició de l'usuari */}
          {posUsuari && (
            <Marker position={posUsuari} icon={icoUbicacio}>
              <Popup>La teva ubicació</Popup>
            </Marker>
          )}

          {/* Polilínia de la ruta */}
          {!legsData && routeCoords.length > 1 && (
            <Polyline positions={routeCoords} color="#007AFF" weight={3} dashArray="8,4" />
          )}
          {/* BUG1: línia verda puntejada d'INICI al primer punt quan hi ha legs del planner */}
          {legsData && legOffset > 0 && (() => {
            const a = puntsRuta[0]
            const b = puntsRuta[legOffset]
            if (!a?.lat || !a?.lng || !b?.lat || !b?.lng) return null
            return <Polyline key="poly-inici-first" positions={[[a.lat, a.lng], [b.lat, b.lng]]} color="#34C759" weight={2} dashArray="4,8" />
          })()}
          {legsData && legsData.flatMap((leg, legIdx) => {
            // Leg correspon a puntsRuta[legOffset + legIdx] → puntsRuta[legOffset + legIdx + 1]
            if (!leg?.itinerari?.segments) {
              const a = puntsRuta[legOffset + legIdx]
              const b = puntsRuta[legOffset + legIdx + 1]
              if (!a?.lat || !b?.lat) return []
              return [<Polyline key={`poly-${legIdx}`} positions={[[a.lat, a.lng], [b.lat, b.lng]]} color="#007AFF" weight={3} dashArray="8,4" />]
            }
            return leg.itinerari.segments.map((seg, segIdx) => {
              const positions = [[seg.origen.lat, seg.origen.lng], [seg.desti.lat, seg.desti.lng]]
              if (seg.tipus === 'WALK') {
                return <Polyline key={`poly-${legIdx}-${segIdx}`} positions={positions} color="#888888" weight={2} dashArray="6,6" />
              }
              return <Polyline key={`poly-${legIdx}-${segIdx}`} positions={positions} color={seg.color || '#007AFF'} weight={4} />
            })
          })}

          {/* Marcadors de parades TMB */}
          {mostrarParades && paradesMap.map(parada => {
            const arrivals = paradaArrivals[parada.id]
            return (
              <Marker key={`parada-${parada.id}`} position={[parada.lat, parada.lng]}
                icon={createStopIcon(parada.lines)}
                eventHandlers={{ click: () => handleParadaClick(parada) }}>
                <Popup minWidth={180}>
                  <div style={{ minWidth: 180 }}>
                    <strong style={{ fontSize: 13, display: 'block', marginBottom: 4 }}>🚌 {parada.nom}</strong>
                    {parada.lines?.length > 0 && (
                      <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>
                        Línies: {parada.lines.join(', ')}
                      </div>
                    )}

                    {loadingParada === parada.id && (
                      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 6 }}>⏳ Carregant horaris…</div>
                    )}

                    {arrivals && arrivals.length === 0 && (
                      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 6 }}>Sense dades disponibles</div>
                    )}

                    {arrivals && arrivals.length > 0 && (
                      <div style={{ marginTop: 6 }}>
                        <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4, color: '#374151' }}>⏱️ Pròxims busos:</div>
                        {arrivals.slice(0, 4).map((a, idx) => (
                          <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12, marginBottom: 3 }}>
                            <span style={{ fontWeight: 700, minWidth: 28 }}>{a.linia}</span>
                            <span style={{ flex: 1, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.destinacio}</span>
                            <span style={{ fontWeight: 600, color: a.minuts <= 2 ? '#FF3B30' : a.minuts <= 5 ? '#FF9500' : '#34C759' }}>
                              {a.minuts === 0 ? 'Ara' : `${a.minuts}'`}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}

                    {!arrivals && loadingParada !== parada.id && (
                      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 6 }}>Toca per veure horaris</div>
                    )}

                    <div style={{ marginTop: 8, borderTop: '1px solid #f3f4f6', paddingTop: 6 }}>
                      <button onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${parada.lat},${parada.lng}`, '_blank')}
                        style={{ fontSize: 11, color: '#007AFF', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                        📍 Veure a Google Maps
                      </button>
                    </div>
                  </div>
                </Popup>
              </Marker>
            )
          })}

          {/* Marcadors de tots els punts de la seqüència */}
          {(() => {
            let domNum = 0
            return puntsRuta.map((punt, i) => {
              if (!punt.lat || !punt.lng) return null

              if (punt.tipus === 'inici') {
                return (
                  <Marker key={`punt-${i}`} position={[punt.lat, punt.lng]} icon={createStartIcon()}>
                    <Popup minWidth={160}>
                      <div style={S.popup}>
                        <strong style={S.popupCodi}>🏠 Punt d'inici</strong>
                        {punt.adreca && <p style={S.popupAdreca}>{punt.adreca}</p>}
                      </div>
                    </Popup>
                  </Marker>
                )
              }

              if (punt.tipus === 'hospital') {
                return (
                  <Marker key={`punt-${i}`} position={[punt.lat, punt.lng]} icon={createHospitalIcon()}>
                    <Popup minWidth={160}>
                      <div style={S.popup}>
                        <strong style={{ ...S.popupCodi, color: '#FF3B30' }}>🏥 {punt.nom ?? 'Hospital'}</strong>
                        {punt.adreca && <p style={S.popupAdreca}>{punt.adreca}</p>}
                      </div>
                    </Popup>
                  </Marker>
                )
              }

              domNum++
              const dom = punt.id ? (domicilisRuta.find(d => d.id === punt.id) ?? punt) : punt
              const estat = punt.dades_json?.estat ?? dom.dades_json?.estat
              return (
                <Marker key={`punt-${i}`} position={[punt.lat, punt.lng]}
                  icon={createNumberedIcon(domNum, estat)}>
                  <Popup minWidth={200}>
                    <div style={S.popup}>
                      <strong style={{ ...S.popupCodi, color: ESTAT_COLORS[estat] ?? colorOrdre(domNum - 1) }}>
                        {domNum}. {punt.codi}
                      </strong>
                      {estat === 'ingres' && <span style={S.popupBadgeIngres}>🟢 INGRÉS</span>}
                      {estat === 'alta'   && <span style={S.popupBadgeAlta}>🔴 ALTA</span>}
                      {punt.nom && <p style={S.popupAdreca}>👤 {punt.nom}</p>}
                      {dom.dades_json?.telefon && (
                        <p style={S.popupAdreca}>
                          <a href={`tel:${dom.dades_json.telefon.replace(/\s/g, '')}`}
                             style={{ color: '#1d4ed8', textDecoration: 'none' }}>
                            📞 {dom.dades_json.telefon}
                          </a>
                        </p>
                      )}
                      {punt.adreca && <p style={S.popupAdreca}>{punt.adreca}</p>}
                      {dom.dades_json?.notes && <p style={S.popupNotes}>{dom.dades_json.notes}</p>}
                      <div style={S.popupBtns}>
                        <button style={S.btnGuiar} onClick={() => guiar(dom)}>🧭 Guiar</button>
                        <button style={S.btnTaxi}  onClick={() => setTaxiDom(dom)}>🚕 Taxi</button>
                      </div>
                    </div>
                  </Popup>
                </Marker>
              )
            })
          })()}
        </MapContainer>

        {/* Toggle parades TMB */}
        {showPanel && (
          <button
            style={{ ...S.toggleParadesBtn, borderColor: mostrarParades ? '#E3000B' : '#ccc', backgroundColor: mostrarParades ? '#fff5f5' : '#fff' }}
            onClick={() => setMostrarParades(v => !v)}
            title="Mostrar parades TMB"
          >
            🚌
          </button>
        )}

        {/* GPS: botó discret quan desconegut, re-centre quan actiu, res quan denegat */}
        {gpsEstat === 'desconegut' && (
          <button style={S.gpsHintBtn} onClick={activarUbicacio} title="Activar ubicació">
            📍
          </button>
        )}
        {gpsEstat === 'actiu' && (
          <button
            style={S.geoBtn}
            onClick={() => posUsuari && setFlyTo({ lat: posUsuari[0], lng: posUsuari[1] })}
            aria-label="Centrar ubicació"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <circle cx="12" cy="12" r="3" />
              <path d="M12 1v4M12 19v4M1 12h4M19 12h4" />
            </svg>
          </button>
        )}
      </div>

      {/* Panell inferior d'itinerari */}
      {showPanel && (
        <ItinerariPanel
          domicilis={domicilisRuta}
          sequencia={sequenciaData}
          ruta={rutaSeleccionada}
          legs={legsData}
          onFlyTo={(lat, lng) => setFlyTo({ lat, lng })}
          onGuiar={guiar}
          onTaxi={dom => setTaxiDom(dom)}
          onTmb={dom => setTmbDom(dom)}
        />
      )}

      {taxiDom && <TaxiModal domicili={taxiDom} onClose={() => setTaxiDom(null)} />}
      {tmbDom  && <TmbModal  domicili={tmbDom}  onClose={() => setTmbDom(null)} />}
    </div>
  )
}

const S = {
  // ── Layout ──────────────────────────────────────────────────────────────────
  page:       { height: '100%', display: 'flex', flexDirection: 'column' },
  topBar:     { display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', backgroundColor: '#fff', borderBottom: '1px solid #e5e7eb', zIndex: 500, flexShrink: 0 },
  mapWrapper: { position: 'relative', minHeight: 0 },
  map:        { height: '100%', width: '100%' },

  // ── TopBar ──────────────────────────────────────────────────────────────────
  backBtn:    { background: 'none', border: '1px solid #e5e7eb', borderRadius: 8, padding: '6px 10px', fontSize: 16, color: '#374151', cursor: 'pointer', flexShrink: 0 },
  rutaSelect: { flex: 1, padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, backgroundColor: '#fafafa', outline: 'none', minWidth: 0 },
  comptador:  { fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap', backgroundColor: '#f3f4f6', padding: '4px 8px', borderRadius: 999 },

  // ── Botons flotants del mapa ─────────────────────────────────────────────────
  toggleParadesBtn: { position: 'absolute', top: 10, right: 10, zIndex: 1000, background: '#fff', border: '2px solid #ccc', borderRadius: 8, padding: '8px 10px', fontSize: 18, cursor: 'pointer', boxShadow: '0 2px 6px rgba(0,0,0,0.2)' },
  geoBtn:     { position: 'absolute', bottom: 12, right: 12, zIndex: 1000, width: 42, height: 42, borderRadius: '50%', backgroundColor: '#fff', border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#374151' },
  gpsHintBtn: { position: 'absolute', bottom: 62, right: 12, zIndex: 1000, width: 36, height: 36, borderRadius: '50%', backgroundColor: '#fff', border: '1px solid #ddd', boxShadow: '0 2px 4px rgba(0,0,0,0.15)', cursor: 'pointer', fontSize: 16, opacity: 0.8, display: 'flex', alignItems: 'center', justifyContent: 'center' },

  // ── Panell itinerari ────────────────────────────────────────────────────────
  panel:       { display: 'flex', flexDirection: 'column', backgroundColor: '#fff', borderTop: '2px solid #e5e7eb', flex: 2, overflow: 'hidden' },
  panelHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 16px', borderBottom: '1px solid #f3f4f6', flexShrink: 0 },
  panelTitol:  { fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em' },
  panelHora:   { fontSize: 13, color: '#374151' },
  panelLlista: { flex: 1, overflowY: 'auto', padding: '2px 0 16px' },
  panelTotal:  { padding: '10px 16px', fontSize: 13, color: '#374151', borderTop: '1px solid #f3f4f6', flexShrink: 0 },

  // ── Fila de parada ──────────────────────────────────────────────────────────
  paradaBloc:  { borderBottom: '1px solid #f9fafb' },
  paradaFila:  { display: 'flex', alignItems: 'flex-start', gap: 10, padding: '9px 16px', cursor: 'pointer', WebkitTapHighlightColor: 'transparent' },
  paradaNum:   { width: 26, height: 26, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: '#fff', flexShrink: 0, marginTop: 1 },
  paradaInfo:  { flex: 1, minWidth: 0 },
  paradaCodi:  { fontSize: 13, fontWeight: 600, color: '#111827', display: 'block' },
  paradaAdreca:{ fontSize: 11, color: '#6b7280', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  paradaNom:   { fontSize: 12, color: '#374151', display: 'block', marginTop: 2 },
  paradaTel:   { fontSize: 12, color: '#1d4ed8', display: 'block', marginTop: 1, textDecoration: 'none' },
  badgeIngres: { display: 'inline-block', marginTop: 3, padding: '2px 7px', borderRadius: 12, fontSize: 10, fontWeight: 600, backgroundColor: '#d1fae5', color: '#065f46' },
  badgeAlta:   { display: 'inline-block', marginTop: 3, padding: '2px 7px', borderRadius: 12, fontSize: 10, fontWeight: 600, backgroundColor: '#fee2e2', color: '#991b1b' },
  paradaBtns:  { display: 'flex', gap: 4, flexShrink: 0 },
  btnPetit:    { padding: '4px 6px', border: '1px solid #e5e7eb', borderRadius: 6, backgroundColor: '#f9fafb', cursor: 'pointer', fontSize: 13, lineHeight: 1 },

  // ── Segment entre parades ───────────────────────────────────────────────────
  segmentFila: { paddingLeft: 52, paddingBottom: 4 },
  segmentText: { fontSize: 11, color: '#9ca3af' },

  // ── Segments planner ────────────────────────────────────────────────────────
  plannerBloc:    { paddingLeft: 42, paddingRight: 12, paddingBottom: 6 },
  plannerSeg:     { display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 4 },
  plannerBarra:   { width: 3, minHeight: 32, borderRadius: 2, flexShrink: 0, marginTop: 4 },
  plannerInfo:    { flex: 1, minWidth: 0 },
  plannerTipus:   { fontSize: 12, color: '#111827', display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' },
  plannerLinia:   { color: '#fff', fontSize: 10, fontWeight: 700, borderRadius: 4, padding: '1px 5px' },
  plannerTemps:   { fontSize: 11, color: '#9ca3af', display: 'block', marginTop: 1 },
  plannerRealTime:{ color: '#f59e0b', fontWeight: 600 },
  plannerResum:   { fontSize: 11, color: '#6b7280', paddingTop: 2, borderTop: '1px solid #f3f4f6', marginTop: 4 },

  // ── Popup del marcador ──────────────────────────────────────────────────────
  popup:           { minWidth: 180 },
  popupCodi:       { fontSize: 14, fontWeight: 700, display: 'block', marginBottom: 4 },
  popupBadgeIngres:{ display: 'inline-block', fontSize: 10, fontWeight: 700, color: '#166534', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 999, padding: '1px 6px', marginBottom: 4 },
  popupBadgeAlta:  { display: 'inline-block', fontSize: 10, fontWeight: 700, color: '#991b1b', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: 999, padding: '1px 6px', marginBottom: 4 },
  popupAdreca: { margin: '0 0 4px', fontSize: 13, color: '#374151' },
  popupNotes:  { margin: '0 0 8px', fontSize: 12, color: '#9ca3af', fontStyle: 'italic' },
  popupBtns:   { display: 'flex', gap: 6 },
  btnGuiar:    { flex: 1, padding: '6px', backgroundColor: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 500 },
  btnTaxi:     { flex: 1, padding: '6px', backgroundColor: '#fefce8', color: '#92400e', border: '1px solid #fde68a', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 500 },

  // ── Modals ──────────────────────────────────────────────────────────────────
  modalFons:   { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 3000, display: 'flex', alignItems: 'flex-end' },
  modalSheet:  { backgroundColor: '#fff', borderRadius: '20px 20px 0 0', padding: '12px 20px 32px', width: '100%' },
  handle:      { width: 36, height: 4, backgroundColor: '#e5e7eb', borderRadius: 2, margin: '0 auto 16px' },
  modalTitol:  { margin: '0 0 4px', fontSize: 17, fontWeight: 700, color: '#111827' },
  modalSub:    { margin: '0 0 16px', fontSize: 13, color: '#6b7280' },
  opcioBtn:    { width: '100%', display: 'flex', alignItems: 'center', gap: 14, padding: '14px 8px', backgroundColor: 'transparent', border: 'none', borderBottom: '1px solid #f3f4f6', cursor: 'pointer', textAlign: 'left' },
  btnCancel:   { width: '100%', marginTop: 8, padding: '12px', backgroundColor: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 500, cursor: 'pointer' },

  // ── Modal TMB ───────────────────────────────────────────────────────────────
  tmbMsg:   { color: '#9ca3af', fontSize: 14, margin: '12px 0' },
  stopFila: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f3f4f6' },
  stopNom:  { fontSize: 14, color: '#111827', flex: 1 },
  stopDist: { fontSize: 13, color: '#9ca3af', marginLeft: 8 },
}
